import type {
  Archetype,
  GraphRecords,
  MaterialNodeSpec,
  OperationNodeSpec,
} from '../../../graph/domain/types';

/**
 * The topological shape an archetype imposes on the graph.
 *
 * TRANSFORM and MEASURE are indistinguishable here — both are 1-in / 1-out and
 * collapse into a single labeled edge — so converting between them needs no
 * rewiring at all. Only three shapes actually exist.
 */
export type TopologyClass = 'SIMPLE' | 'TRANSFER' | 'COMPLEX';

export function topologyClassOf(archetype: Archetype): TopologyClass {
  if (archetype === 'TRANSFER') return 'TRANSFER';
  if (archetype === 'COMPLEX') return 'COMPLEX';
  return 'SIMPLE';
}

/** A flow edge as seen from one operation, keeping its Record key and order. */
export interface OperationEdgeRef {
  /** Key under which the edge lives in `graphData.flowEdges`. */
  edgeKey: string;
  /** The edge's own id (falls back to the Record key). */
  edgeId: string;
  /** The material on the far side of the edge. */
  materialId: string;
}

/**
 * Input edges (material → operation) in Record insertion order.
 *
 * Order matters: both `hydrateFromSnapshot` (Step D2) and the backend's
 * `graphHelperTransforms` read `inputs[0]` / `inputs[1]` positionally to
 * recover a TRANSFER's source and destination.
 */
export function inputEdgesOf(
  graphData: Pick<GraphRecords, 'materialNodes' | 'flowEdges'>,
  operationId: string,
): OperationEdgeRef[] {
  const refs: OperationEdgeRef[] = [];
  for (const [edgeKey, edge] of Object.entries(graphData.flowEdges)) {
    if (edge.targetId !== operationId) continue;
    if (!graphData.materialNodes[edge.sourceId]) continue;
    refs.push({ edgeKey, edgeId: edge.id ?? edgeKey, materialId: edge.sourceId });
  }
  return refs;
}

/** Output edges (operation → material) in Record insertion order. */
export function outputEdgesOf(
  graphData: Pick<GraphRecords, 'materialNodes' | 'flowEdges'>,
  operationId: string,
): OperationEdgeRef[] {
  const refs: OperationEdgeRef[] = [];
  for (const [edgeKey, edge] of Object.entries(graphData.flowEdges)) {
    if (edge.sourceId !== operationId) continue;
    if (!graphData.materialNodes[edge.targetId]) continue;
    refs.push({ edgeKey, edgeId: edge.id ?? edgeKey, materialId: edge.targetId });
  }
  return refs;
}

/** Every flow edge key touching the operation, including `graphsLink` edges. */
export function allEdgeKeysOf(
  graphData: Pick<GraphRecords, 'flowEdges'>,
  operationId: string,
): string[] {
  const keys: string[] = [];
  for (const [edgeKey, edge] of Object.entries(graphData.flowEdges)) {
    if (edge.sourceId === operationId || edge.targetId === operationId) {
      keys.push(edgeKey);
    }
  }
  return keys;
}

export function isReminderMaterial(material: MaterialNodeSpec | undefined): boolean {
  if (!material) return false;
  return Boolean(material.isReminder) || material.symbolicQuantity === 'remainder';
}

/**
 * Operations other than `excludeOperationId` that produce this material.
 * A material with another producer must never be deleted by a conversion.
 */
export function otherProducersOf(
  graphData: Pick<GraphRecords, 'operationNodes' | 'flowEdges'>,
  materialId: string,
  excludeOperationId: string,
): string[] {
  const producers: string[] = [];
  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.targetId !== materialId) continue;
    if (edge.sourceId === excludeOperationId) continue;
    if (!graphData.operationNodes[edge.sourceId]) continue;
    producers.push(edge.sourceId);
  }
  return producers;
}

/** Operations that consume this material, excluding `excludeOperationId`. */
export function consumersOf(
  graphData: Pick<GraphRecords, 'operationNodes' | 'flowEdges'>,
  materialId: string,
  excludeOperationId?: string,
): string[] {
  const consumers: string[] = [];
  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.sourceId !== materialId) continue;
    if (excludeOperationId && edge.targetId === excludeOperationId) continue;
    if (!graphData.operationNodes[edge.targetId]) continue;
    consumers.push(edge.targetId);
  }
  return consumers;
}

export interface DownstreamCascade {
  materialIds: string[];
  operationIds: string[];
}

/**
 * Everything reachable downstream of `seedMaterialIds`, seeds included.
 *
 * Mirrors the cascade loop in `computeDeletePlan` but is seeded from materials
 * and never walks upstream — the converting operation and its other branches
 * must survive.
 */
export function collectDownstreamCascade(
  graphData: Pick<GraphRecords, 'materialNodes' | 'operationNodes' | 'flowEdges'>,
  seedMaterialIds: string[],
): DownstreamCascade {
  const materials = new Set<string>();
  const operations = new Set<string>();
  const queue: string[] = [];
  const allEdges = Object.values(graphData.flowEdges);

  for (const seed of seedMaterialIds) {
    if (!graphData.materialNodes[seed]) continue;
    if (materials.has(seed)) continue;
    materials.add(seed);
    queue.push(seed);
  }

  let index = 0;
  while (index < queue.length) {
    const materialId = queue[index++];

    for (const edge of allEdges) {
      if (edge.sourceId !== materialId) continue;
      const consumerId = edge.targetId;
      if (!graphData.operationNodes[consumerId]) continue;
      if (operations.has(consumerId)) continue;
      operations.add(consumerId);

      for (const outEdge of allEdges) {
        if (outEdge.sourceId !== consumerId) continue;
        if (!graphData.materialNodes[outEdge.targetId]) continue;
        if (materials.has(outEdge.targetId)) continue;
        materials.add(outEdge.targetId);
        queue.push(outEdge.targetId);
      }
    }
  }

  return {
    materialIds: Array.from(materials),
    operationIds: Array.from(operations),
  };
}

/**
 * The input that best represents the operation when narrowing to a single one.
 *
 * Uses the same precedence as `resolveEntryAnchor` in `cycleAnchors.ts` so that
 * a conversion keeps whichever material the cycle machinery already treats as
 * the operation's primary input.
 */
export function resolvePrimaryInput(
  graphData: Pick<GraphRecords, 'materialNodes' | 'flowEdges'>,
  operation: OperationNodeSpec,
  operationId: string,
): string | null {
  const inputs = inputEdgesOf(graphData, operationId);
  if (inputs.length === 0) return null;

  if (operation.archetype === 'TRANSFER') {
    const sourceId = operation.transferRow?.sourceMaterialId;
    if (sourceId && inputs.some((i) => i.materialId === sourceId)) return sourceId;
  }

  if (operation.archetype === 'COMPLEX') {
    const mapping = operation.complexOptions?.ioMapping ?? [];
    const totals = new Map<string, number>();
    for (const entry of mapping) {
      totals.set(
        entry.inputMaterialId,
        (totals.get(entry.inputMaterialId) ?? 0) + entry.fraction,
      );
    }
    let best: string | null = null;
    let bestFraction = 0;
    for (const input of inputs) {
      const fraction = totals.get(input.materialId) ?? 0;
      if (fraction > bestFraction) {
        best = input.materialId;
        bestFraction = fraction;
      }
    }
    if (best) return best;
  }

  return inputs[0].materialId;
}

/**
 * The output that best represents the operation when narrowing to a single one.
 * Reminder materials are never primary — they are bookkeeping, not product.
 */
export function resolvePrimaryOutput(
  graphData: Pick<GraphRecords, 'materialNodes' | 'flowEdges'>,
  operation: OperationNodeSpec,
  operationId: string,
): string | null {
  const outputs = outputEdgesOf(graphData, operationId);
  if (outputs.length === 0) return null;

  const nonReminder = outputs.filter(
    (o) => !isReminderMaterial(graphData.materialNodes[o.materialId]),
  );
  const candidates = nonReminder.length > 0 ? nonReminder : outputs;

  if (operation.archetype === 'TRANSFER') {
    const destPost = operation.transferRow?.destinationPostMaterialId;
    if (destPost && candidates.some((o) => o.materialId === destPost)) return destPost;
  }

  if (operation.archetype === 'COMPLEX') {
    const mapping = operation.complexOptions?.ioMapping ?? [];
    const totals = new Map<string, number>();
    for (const entry of mapping) {
      totals.set(
        entry.outputMaterialId,
        (totals.get(entry.outputMaterialId) ?? 0) + entry.fraction,
      );
    }
    let best: string | null = null;
    let bestFraction = 0;
    for (const output of candidates) {
      const fraction = totals.get(output.materialId) ?? 0;
      if (fraction > bestFraction) {
        best = output.materialId;
        bestFraction = fraction;
      }
    }
    if (best) return best;
  }

  return candidates[0].materialId;
}
